import React, { createContext, useContext, useState, useEffect } from 'react';

// Default Clinical Blue Theme
const defaultTheme = {
  // Core colors
  primaryColor: '#0066CC',       // Clinical Blue
  primaryHover: '#0052A3',       // Darker blue for hover
  primaryLight: '#E6F0FA',       // Light blue background
  
  // Backgrounds
  backgroundColor: '#FFFFFF',    // White
  surfaceColor: '#F8FAFC',       // Subtle light gray for cards/sections
  
  // Navigation
  headerBackground: '#0066CC',   // Clinical blue header
  menuBackground: '#F8FAFC',     // Light sidebar (doctor dashboard)
  adminHeaderBackground: '#1E293B', // Dark slate for admin
  adminMenuBackground: '#1E293B',   // Dark slate sidebar for admin
  
  // Text
  textPrimary: '#1E293B',        // Dark slate for primary text
  textSecondary: '#64748B',      // Muted gray for secondary text
  textOnPrimary: '#FFFFFF',      // White text on primary color
  
  // Accent and state colors (kept as system defaults)
  successColor: '#10B981',       // Green
  warningColor: '#F59E0B',       // Amber
  errorColor: '#EF4444',         // Red
  infoColor: '#3B82F6',          // Blue
  
  // Borders
  borderColor: '#E2E8F0',        // Light gray border
  
  // Focus/Ring
  focusRingColor: '#0066CC',     // Clinical blue focus
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Load saved theme from localStorage
    try {
      const savedTheme = localStorage.getItem('exlva_dare_theme');
      if (savedTheme) {
        return { ...defaultTheme, ...JSON.parse(savedTheme) };
      }
    } catch (error) {
      console.error('Error loading theme:', error);
    }
    return defaultTheme;
  });

  // Apply theme to CSS variables whenever theme changes
  useEffect(() => {
    applyThemeToDOM(theme);
    // Save to localStorage
    localStorage.setItem('exlva_dare_theme', JSON.stringify(theme));
  }, [theme]);

  const applyThemeToDOM = (themeObj) => {
    const root = document.documentElement;
    
    // Primary colors
    root.style.setProperty('--theme-primary', themeObj.primaryColor);
    root.style.setProperty('--theme-primary-hover', themeObj.primaryHover);
    root.style.setProperty('--theme-primary-light', themeObj.primaryLight);
    
    // Backgrounds
    root.style.setProperty('--theme-background', themeObj.backgroundColor);
    root.style.setProperty('--theme-surface', themeObj.surfaceColor);
    
    // Navigation
    root.style.setProperty('--theme-header-bg', themeObj.headerBackground);
    root.style.setProperty('--theme-menu-bg', themeObj.menuBackground);
    root.style.setProperty('--theme-admin-header-bg', themeObj.adminHeaderBackground);
    root.style.setProperty('--theme-admin-menu-bg', themeObj.adminMenuBackground);
    
    // Text
    root.style.setProperty('--theme-text-primary', themeObj.textPrimary);
    root.style.setProperty('--theme-text-secondary', themeObj.textSecondary);
    root.style.setProperty('--theme-text-on-primary', themeObj.textOnPrimary);
    
    // State colors
    root.style.setProperty('--theme-success', themeObj.successColor);
    root.style.setProperty('--theme-warning', themeObj.warningColor);
    root.style.setProperty('--theme-error', themeObj.errorColor);
    root.style.setProperty('--theme-info', themeObj.infoColor);
    
    // Borders
    root.style.setProperty('--theme-border', themeObj.borderColor);
    
    // Focus
    root.style.setProperty('--theme-focus-ring', themeObj.focusRingColor);
  };

  const updateTheme = (newTheme) => {
    setTheme(prev => ({ ...prev, ...newTheme }));
  };

  const resetToDefault = () => {
    setTheme(defaultTheme);
    localStorage.removeItem('exlva_dare_theme');
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, resetToDefault, defaultTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
